import { useEffect, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { RequestTypeRestrictionStatus } from '@atlassian/jira-servicedesk-ticket-types-store/src/common/types.tsx';
import { useDeleteRequestTypeRestriction } from '../../services/delete-request-type-restriction/index.tsx';
import { useLoadRequestTypeRestriction } from '../../services/load-request-type-restriction/index.tsx';
import type { RequestTypeSupportedPrincipal } from '../../services/load-request-type-restriction/types.tsx';
import { useSaveRequestTypeRestriction } from '../../services/save-request-type-restriction/index.tsx';

export const useRequestTypePermissions = (projectId: number, requestTypeExternalId: string) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		data: configurationData,
		loading: configurationLoading,
		error: configurationError,
		fetch: configurationFetch,
	} = useLoadRequestTypeRestriction(projectId, requestTypeExternalId);
	const { loading: saveLoading, fetch: saveFetch } = useSaveRequestTypeRestriction(
		projectId,
		requestTypeExternalId,
	);
	const { loading: deleteLoading, fetch: deleteFetch } = useDeleteRequestTypeRestriction(
		projectId,
		requestTypeExternalId,
	);

	const [principals, setPrincipals] = useState<RequestTypeSupportedPrincipal[]>([]);

	useEffect(() => {
		if (configurationData) {
			setPrincipals(configurationData.principals);
		}
	}, [configurationData]);

	const prependPrincipals = (newPrincipals: RequestTypeSupportedPrincipal[]) => {
		setPrincipals((existingPrincipals) => [...newPrincipals, ...existingPrincipals]);
	};

	const removePrincipal = (id: string) => {
		setPrincipals(principals.filter((principal) => principal.id !== id));
	};

	const fireAnalytics = (result: 'succeed' | 'fail') => {
		const oldPrincipals = configurationData ? configurationData.principals : [];
		const attributes = {
			restrictionAction: principals.length === 0 ? 'OPEN' : 'RESTRICTED',
			restrictionPrincipalsCount: principals.length,
			restrictionIndividualsCount: principals.filter((p) => p.type === 'USER').length,
			restrictionPrincipalsAdded: principals.filter(
				(newP) => !oldPrincipals.some((oldP) => oldP.id === newP.id),
			).length,
			restrictionPrincipalsRemoved: oldPrincipals.filter(
				(oldP) => !principals.some((newP) => newP.id === oldP.id),
			).length,
			restrictionGroupsCount: principals.filter((p) => p.type === 'USER_GROUP').length,
			restrictionCustomerOrganizationsCount: principals.filter(
				(p) => p.type === 'CUSTOMER_ORGANIZATION',
			).length,
		};
		return result === 'succeed'
			? fireTrackAnalytics(createAnalyticsEvent({}), 'requestTypePermissions updated', attributes)
			: fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'requestTypePermissions updateFailed',
					attributes,
				);
	};

	/**
	 * If response was successful, we expect null to be returned as the body is empty.
	 * Otherwise, we expect undefined or something else in the error case, which we convert to an exception.
	 */
	const applyConfiguration = async (): Promise<RequestTypeRestrictionStatus> => {
		if (principals.length === 0) {
			const result = await deleteFetch();
			if (result !== null) {
				fireAnalytics('fail');
				throw Error('Failed to delete configuration');
			}
			fireAnalytics('succeed');
			return 'OPEN';
		}

		const result = await saveFetch({ principals: principals.map(({ id }) => id) });
		if (result !== null) {
			fireAnalytics('fail');
			throw Error('Failed to save configuration');
		}
		fireAnalytics('succeed');
		return 'RESTRICTED';
	};

	return {
		principals,
		prependPrincipals,
		removePrincipal,
		requestTypePermissionLoading: configurationLoading,
		requestTypePermissionError: configurationError,
		requestTypePermissionFetch: configurationFetch,
		applyRequestTypePermission: applyConfiguration,
		applyRequestTypePermissionLoading: saveLoading || deleteLoading,
	};
};
